import {DispatchCarryover, DispatchEntity, DispatchNotificationEntry, dispatchStatus, Message} from "@ots-frontend/ots-data";
import {StatusTone} from "../../components/StatusPill";
import {getNotificationSkipReasonLabel} from "../../dispatch/dispatch-notification-labels";

export type DispatchActivityKind = "comment" | "carryover" | "lifecycle" | "notification";

/** One entry of the dispatch activity log: a comment, a carryover, a status change or a driver notification. */
export type DispatchActivityItem = {
  id: string;
  kind: DispatchActivityKind;
  dttm: number;
  title: string;
  body?: string;
  author?: string;
  /** Message type of a comment, e.g. "Comment" or "Internal". */
  messageType?: string;
  tone: StatusTone;
};

export type DispatchLifecycleEvent = {
  id: string;
  label: string;
  dttm?: number;
  done: boolean;
  tone: StatusTone;
};

const statusCode = (dispatch: Pick<DispatchEntity, "status">) => dispatch.status?.code;

export const isDispatchClosed = (dispatch: Pick<DispatchEntity, "status">) =>
  statusCode(dispatch) === dispatchStatus.completed.code || statusCode(dispatch) === dispatchStatus.canceled.code;

export const getDispatchStatusTone = (dispatch: Pick<DispatchEntity, "status" | "pickupTransportId">): StatusTone => {
  switch (statusCode(dispatch)) {
    case dispatchStatus.completed.code:
      return "success";
    case dispatchStatus.canceled.code:
      return "danger";
    case dispatchStatus.started.code:
      return "warning";
    default:
      return "info";
  }
};

/** Started dispatches with a recorded pickup read as picked up, matching the dispatch grid. */
export const getDispatchStatusLabel = (dispatch: Pick<DispatchEntity, "status" | "pickupTransportId">): string =>
  statusCode(dispatch) === dispatchStatus.started.code && dispatch.pickupTransportId
    ? "Picked-Up"
    : statusCode(dispatch) ?? "Unknown";

export const getDispatchTitle = (dispatch: Pick<DispatchEntity, "workOrderNumber" | "ticketNumber">): string =>
  dispatch.workOrderNumber
    ? `Dispatch ${dispatch.workOrderNumber}`
    : dispatch.ticketNumber
    ? `Dispatch ticket ${dispatch.ticketNumber}`
    : "Dispatch";

/**
 * The steps a dispatch moves through, with the ones it has reached marked done. A dispatch made from a request
 * starts with the request being made, authorized and accepted. Times come from the dispatch and its tickets: the driver's acknowledgement, pickup and delivery (ticket time, else departure), arrivals when recorded, and when the current status was
 * set (started, completed or canceled). Steps without a recorded time show without one.
 */
export const getDispatchLifecycle = (dispatch: DispatchEntity): DispatchLifecycleEvent[] => {
  const code = statusCode(dispatch);
  const canceled = code === dispatchStatus.canceled.code;
  const completed = code === dispatchStatus.completed.code;
  const startedNow = code === dispatchStatus.started.code;
  // ticket times first (what the old timeline showed), then the dispatch's own departure times
  const pickupDttm = dispatch.pickupDttm ?? dispatch.outDttm;
  const deliveryDttm = dispatch.deliveryDttm ?? dispatch.deliveryOutDttm;
  const pickedUp = !!dispatch.pickupTransportId || pickupDttm != null;
  const delivered = !!dispatch.deliveryTransportId || deliveryDttm != null;

  const request = dispatch.dispatchRequest;
  const requestEvents: DispatchLifecycleEvent[] = request ? [
    {id: "requested", label: "Requested", dttm: request.createDttm, done: true, tone: "neutral"},
    ...(request.authorizeDttm != null ? [{id: "authorized", label: "Request authorized", dttm: request.authorizeDttm, done: true, tone: "info" as const}] : []),
    ...(request.acceptedDttm != null ? [{id: "accepted", label: "Request accepted", dttm: request.acceptedDttm, done: true, tone: "info" as const}] : []),
  ] : [];

  const events: DispatchLifecycleEvent[] = [
    ...requestEvents,
    {id: "created", label: request ? "Dispatched" : "Created", dttm: dispatch.createDttm, done: true, tone: "neutral"},
    {id: "assigned", label: dispatch.personName ? `Assigned to ${dispatch.personName}` : "Assigned to a driver", done: !!dispatch.personId, tone: "info"},
    {id: "acknowledged", label: "Driver acknowledged", dttm: dispatch.personAckDttm, done: !!dispatch.personAckDttm, tone: "info"},
    {
      id: "started",
      label: "Started",
      dttm: startedNow ? dispatch.statusModifiedDttm : undefined,
      done: startedNow || completed || pickedUp,
      tone: "warning",
    },
    ...(dispatch.inDttm != null ? [{id: "arrived-pickup", label: "Arrived at pickup", dttm: dispatch.inDttm, done: true, tone: "warning" as const}] : []),
    {id: "picked-up", label: "Picked up", dttm: pickupDttm, done: pickedUp, tone: "warning"},
    ...(dispatch.deliveryInDttm != null ? [{id: "arrived-delivery", label: "Arrived at delivery", dttm: dispatch.deliveryInDttm, done: true, tone: "success" as const}] : []),
    {id: "delivered", label: "Delivered", dttm: deliveryDttm, done: delivered, tone: "success"},
  ];

  if (canceled) {
    return [
      ...events.filter(event => event.done),
      {id: "canceled", label: "Canceled", dttm: dispatch.statusModifiedDttm, done: true, tone: "danger"},
    ];
  }

  return [...events, {id: "completed", label: "Completed", dttm: completed ? dispatch.statusModifiedDttm : undefined, done: completed, tone: "success"}];
};

export const describeDispatchNotification = (notification: DispatchNotificationEntry): {title: string; tone: StatusTone} => {
  const driver = notification.dispatchPersonName ?? "the driver";
  switch (notification.status) {
    case "SKIPPED":
      return {
        title: `Notification not sent${notification.skipReason ? ` (${getNotificationSkipReasonLabel(notification.skipReason)})` : ""}`,
        tone: "neutral",
      };
    case "FAILED":
      return {title: `Notification to ${driver} failed`, tone: "danger"};
    default:
      return {title: `Notified ${driver}`, tone: "info"};
  }
};

const describeCarryover = (carryover: DispatchCarryover) => carryover.personName
  ? `Moved to the next day while ${carryover.personName} was off shift.`
  : "Moved to the next day while it was unassigned.";

/** Comments, carryovers, notifications and dated status changes in one list, newest first. */
export const buildDispatchActivity = ({
  dispatch,
  messages = [],
  carryovers = [],
  notifications = [],
}: {
  dispatch: DispatchEntity;
  messages?: Message[];
  carryovers?: DispatchCarryover[];
  notifications?: DispatchNotificationEntry[];
}): DispatchActivityItem[] => {
  const items: DispatchActivityItem[] = [
    ...messages.map((message): DispatchActivityItem => ({
      id: `comment-${message.id}`,
      kind: "comment",
      dttm: message.createDttm,
      title: message.personName ?? "Unknown user",
      body: message.message,
      author: message.personName,
      messageType: message.messageType,
      tone: "neutral",
    })),
    ...carryovers.map((carryover): DispatchActivityItem => ({
      id: `carryover-${carryover.id}`,
      kind: "carryover",
      dttm: carryover.createDttm,
      title: `Carried over (${carryover.carryoverNumber})`,
      body: describeCarryover(carryover),
      tone: "warning",
    })),
    ...notifications.map((notification): DispatchActivityItem => ({
      id: `notification-${notification.id}`,
      kind: "notification",
      dttm: notification.createDttm,
      ...describeDispatchNotification(notification),
      body: notification.createdByName
        ? `${notification.status === "QUEUED" ? "Sent" : "Requested"} by ${notification.createdByName}`
        : undefined,
    })),
    ...getDispatchLifecycle(dispatch)
      .filter(event => event.done && event.dttm != null)
      .map((event): DispatchActivityItem => ({
        id: `lifecycle-${event.id}`,
        kind: "lifecycle",
        dttm: event.dttm as number,
        title: event.label,
        tone: event.tone,
      })),
  ];

  return items.sort((left, right) => right.dttm - left.dttm);
};
